import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { authClient } from "./lib/auth-client";
import { identifyUser } from "./lib/telemetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CuitContext, useCuitProvider } from "./hooks/useCuits";
import { InvoiceJobCenterProvider } from "./hooks/useInvoiceJobs";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import Layout from "./components/Layout";

const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transfers = lazy(() => import("./pages/Transfers"));
const Invoices = lazy(() => import("./pages/Invoices"));
const CreditNotes = lazy(() => import("./pages/CreditNotes"));
const CuitSetup = lazy(() => import("./pages/CuitSetup"));
const CuitUsers = lazy(() => import("./pages/CuitUsers"));
const CuitNew = lazy(() => import("./pages/CuitNew"));
const ArcaStatus = lazy(() => import("./pages/ArcaStatus"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AuthenticatedApp() {
  const cuitCtx = useCuitProvider();

  return (
    <CuitContext.Provider value={cuitCtx}>
      <InvoiceJobCenterProvider>
        <ConfirmDialogProvider>
          <Layout>
            <Suspense
              fallback={
                <div className="flex min-h-[50vh] items-center justify-center text-sm uppercase tracking-widest text-[var(--ink-tertiary)]">
                  Cargando...
                </div>
              }
            >
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/transfers" element={<Transfers />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/credit-notes" element={<CreditNotes />} />
                <Route path="/arca-status" element={<ArcaStatus />} />
                <Route path="/cuit/settings" element={<CuitSetup />} />
                <Route path="/cuit/users" element={<CuitUsers />} />
                <Route path="/cuit/new" element={<CuitNew />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </Layout>
          <Toaster />
        </ConfirmDialogProvider>
      </InvoiceJobCenterProvider>
    </CuitContext.Provider>
  );
}

const fullScreenLoading = (
  <div className="flex min-h-screen items-center justify-center text-sm uppercase tracking-widest text-[var(--ink-tertiary)]">
    Cargando...
  </div>
);

export default function App() {
  const { data: session, isPending } = authClient.useSession();
  const userId = session?.user.id;

  useEffect(() => {
    if (userId) identifyUser(userId);
  }, [userId]);

  if (isPending) return fullScreenLoading;

  return (
    <QueryClientProvider client={queryClient}>
      {session ? (
        <AuthenticatedApp />
      ) : (
        <Suspense fallback={fullScreenLoading}>
          <Routes>
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
